import React, { useCallback, useEffect, useRef, useState } from 'react';
import { graphql, navigate, Link, useStaticQuery } from 'gatsby';

import { MainLink } from './NavDropdownMenus';
import { MobileNav } from './MobileNav';

import Logo from '../../assets/images/logo-small.svg';
import searchIcon from '../../assets/icons/search_black_24dp.svg';
import profileIcon from '../../assets/icons/person_outline_black_24dp.svg';
import cartIcon from '../../assets/icons/shopping_cart_black_24dp.svg';
import menuIcon from '../../assets/icons/menu_black_24dp.svg';

import { useSelector, useDispatch } from 'react-redux';
import { login } from '@src/utils/mmc-api/auth';
import { fetchMiniCart, addToCart } from '../../store/cart';
import { useIsSubscribedBoth } from '@src/hooks/user';

import { WhatsappButton } from '../UI/Whatsapp';
import { useIsMdMin } from '@src/hooks/useBreakpoints';
import { useEdgeAwareDropdown } from '@src/hooks/useEdgeAwareDropdown';
import posthog from 'posthog-js';

export const TOP_NAVIGATION_QUERY = graphql`
  query TopNavigationQuery {
    allDatoCmsNavigation(filter: { name: { in: ["2026 Navigation", "Secondary"] } }) {
      edges {
        node {
          name
          link {
            label
            new
            slug
            externalLink
            subNavigation {
              ... on DatoCmsPage {
                title
                slug
              }
            }
            items {
              __typename
              ... on DatoCmsDropdownMenuItem {
                ...NavDropdownItemFields
              }
              ... on DatoCmsDropdownSection {
                title
                color
                icon
                items {
                  ... on DatoCmsDropdownMenuItem {
                    ...NavDropdownItemFields
                  }
                }
              }
            }
            icon {
              url
            }
          }
        }
      }
    }
  }
`;

const TopNavigation = () => {
  const [isMobNavVisible, setIsMobNavVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const q = searchTerm.trim();
    posthog.capture('resource_search_submitted', { has_query: Boolean(q) });
    navigate(q ? `/search?documents[query]=${encodeURIComponent(q)}` : '/search');
  };

  const user = useSelector((state) => state.user.data);
  const miniCart = useSelector((state) => new Set(state.cart.miniCart));
  const dispatch = useDispatch();

  const data = useStaticQuery(TOP_NAVIGATION_QUERY);
  const navigations = (navName) =>
    data.allDatoCmsNavigation.edges.filter(({ node }) => node.name === navName);
  const mainNav = navigations('2026 Navigation')[0].node;
  const secondaryNav = navigations('Secondary')[0].node;

  const isSubscribedBoth = useIsSubscribedBoth();
  const isMdMin = useIsMdMin();

  const joinText = user?.userId ? 'Upgrade' : 'Join Now';

  const toggleMobileNav = useCallback(() => {
    setIsMobNavVisible((prev) => !prev);
  }, []);

  const closeMobileNav = useCallback(() => setIsMobNavVisible(false), []);

  useEffect(() => {
    let cart;
    if (localStorage?.cart) {
      cart = JSON.parse(localStorage?.cart);
    }
    if (localStorage.cart?.length > 0 && user?.email) {
      dispatch(addToCart(cart, user));
      localStorage.removeItem('cart');
    }
    if (user?.email) {
      dispatch(fetchMiniCart(user));
    }
  }, [user]);

  const desktopNavListRef = useRef(null);
  useEdgeAwareDropdown(desktopNavListRef);

  return (
    <nav className={`top-bar${isMobNavVisible ? ' expanded' : ''}`} data-topbar role="navigation">
      <div className="navigation-wrap">
        {/* Desktop nav */}
        <div
          data-collapse="medium"
          data-animation="default"
          data-duration="400"
          role="banner"
          className="navigation w-nav"
        >
          <div className="navigation-full">
            <div className="nav-top">
              <div className="navigation-container-2">
                <div className="nav-top-container">
                  <div
                    className="flex items-center"
                    style={{ justifyContent: 'space-between', width: '100%' }}
                  >
                    <WhatsappButton />

                    <div className="flex items-center">
                      <div className="nav-top-main-links flex flex-center">
                        {isMdMin ? (
                          <form
                            className="nav-search-form"
                            onSubmit={handleSearchSubmit}
                            role="search"
                          >
                            <input
                              type="search"
                              className="nav-search-input"
                              placeholder="Search"
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              aria-label="Search"
                            />
                            <button
                              type="submit"
                              className="nav-search-button"
                              aria-label="Submit search"
                            >
                              <img src={searchIcon} alt="" />
                            </button>
                          </form>
                        ) : (
                          <Link to="/search" className="nav-search-link" aria-label="Search">
                            <img src={searchIcon} alt="" />
                          </Link>
                        )}
                      </div>

                      <Link to="/cart" className="w-nav-brand">
                        <img src={cartIcon} alt="cartIcon" />
                        {miniCart.size > 0 ? <span className="cartSize">{miniCart.size}</span> : ''}
                      </Link>

                      {user?.email ? (
                        <div style={{ marginRight: 5 }}>
                          <Link
                            to="/profile"
                            className="flex flex-center w-nav-brand profile-link"
                            style={{ gap: 3 }}
                          >
                            <img className="icon" src={profileIcon} alt="profileIcon" />
                            <p>Hi {user.fullname ? user.fullname : user.username}</p>
                          </Link>
                        </div>
                      ) : (
                        <button onClick={login} className="w-nav-brand sign-in-badge">
                          <span>Sign In</span>
                        </button>
                      )}

                      {!isSubscribedBoth && (
                        <button
                          onClick={() => navigate('/subscriptions')}
                          className="w-nav-brand sign-in-badge"
                        >
                          <span>{joinText}</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="navigation-container-2">
              <div className="navigation-left-2">
                <Link to="/" className="brand w-nav-brand w--current">
                  <img src={Logo} width="80" alt="Logo" className="image-2" />
                </Link>
                <div className="nav-wrapper">
                  <section className="top-bar-section">
                    <ul className="left" ref={desktopNavListRef}>
                      {mainNav.link.map((link) => (
                        <MainLink key={link.label} link={link} onCloseMobile={closeMobileNav} />
                      ))}
                    </ul>
                  </section>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile nav bar */}
        <div
          data-collapse="medium"
          data-animation="default"
          data-duration="400"
          data-easing="ease-out"
          data-easing2="ease-out"
          role="banner"
          className="navigation-mob w-nav"
        >
          <div className="navigation-container-mob">
            <Link to="/" className="w-nav-brand">
              <img src={Logo} alt="Logo" />
            </Link>
            <div className="flex flex-center flex-column">
              <div className="mob-container">
                <div>
                  <button className="w-nav-button" onClick={toggleMobileNav}>
                    <img src={menuIcon} alt="menuIcon" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile nav menu */}
        <MobileNav
          mainNav={mainNav}
          // secondaryNav={secondaryNav}
          user={user}
          isOpen={isMobNavVisible}
          onClose={closeMobileNav}
        />
      </div>
    </nav>
  );
};

export { TopNavigation };
